import React, { useMemo, useState } from 'react';
import { IconButton, MenuItem, Typography, Menu, Chip } from '@mui/material';
import { PatternFormat } from 'react-number-format';
import Table from '../common/Table';
import { MoreHoriz } from '@mui/icons-material';
import { GuestHelper } from '../../utils/helpers/guest.helper';
import { getChipConfig } from 'utils/constants/chips';
import MemberChip from 'components/common/MemberChip';
import { excludeHiddenEmails } from 'utils/helpers/email.helper';

const INVITE_STATUS = {
    PENDING_APPROVAL: 'pending_approval',
    INVITED: 'invited'
};

const STATUS_CHIP_CONFIG = {
    [INVITE_STATUS.PENDING_APPROVAL]: {
        label: 'Awaiting approval',
        color: 'warning'
    },
    [INVITE_STATUS.INVITED]: {
        label: 'Invite sent',
        color: 'info'
    }
};

const ROLE_LABELS = {
    operator: 'Org Manager',
    manager: 'Org Manager',
    'virtual guard': 'Virtual Guard',
    resi: 'Resident',
    resident: 'Resident',
    tenant: 'Tenant',
    guest: 'Guest'
};

const formatRoleLabel = role => {
    if (!role) return '—';
    const lookup = ROLE_LABELS[role.toString().toLowerCase()];
    if (lookup) return lookup;
    return role
        .toString()
        .split(/[\s_-]+/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
};

const getStatusChipConfig = (status, source) => {
    if (STATUS_CHIP_CONFIG[status]) return STATUS_CHIP_CONFIG[status];
    if (source === 'manager') return { label: 'Invite sent', color: 'info' };
    return { label: status || 'Pending', color: 'default' };
};

const InvitedOrgMembersTable = ({
    members,
    onInvitedMemberDelete,
    onInvitedMemberApprove,
    invitedManagers,
    onDeleteManager,
    globalFilter,
    canManage
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);

    const data = useMemo(() => {
        const taggedInvited = excludeHiddenEmails(invitedManagers).map(manager => ({
            ...manager,
            role: manager.type, // Normalize 'type' to 'role'
            source: 'manager'
        }));
        const taggedMembers = excludeHiddenEmails(members).map(member => ({
            ...member,
            source: 'member'
        }));
        return [...taggedInvited, ...taggedMembers];
    }, [members, invitedManagers]);

    const handleMenuClick = (event, member) => {
        setAnchorEl(event.currentTarget);
        setSelectedMember(member);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMember(null);
    };

    const handleDeleteClick = () => {
        if (selectedMember.invite_id || selectedMember.source === 'member') {
            onInvitedMemberDelete(selectedMember);
            handleMenuClose();
        } else {
            onDeleteManager(selectedMember);
        }
    };

    const handleApproveClick = () => {
        if (!onInvitedMemberApprove || !selectedMember) return;
        if (selectedMember.source === 'member') {
            onInvitedMemberApprove(selectedMember);
        }
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: row => {
                    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
                    const company = row.company_name || '';
                    return company ? `${name} ${company}` : name;
                },
                Cell: ({ row }) => GuestHelper.formatPerson(row.original)
            },
            {
                Header: 'Phone',
                accessor: row => {
                    if (!row.phone) return '';
                    return `${row.phone.code || ''}${row.phone.number || ''}`.trim();
                },
                Cell: ({ row }) => {
                    const phone = row.original?.phone;

                    if (!phone || !phone.number) {
                        return (
                            <Typography
                                sx={{ fontSize: '13px' }}
                                component="span"
                                color="text.secondary"
                            >
                                N/A
                            </Typography>
                        );
                    }

                    return (
                        <Typography
                            sx={{ fontSize: '13px' }}
                            component="span"
                            color="text.primary"
                        >
                            <PatternFormat
                                value={phone.number}
                                displayType={'text'}
                                format={`+${phone.code || ''} (###)-###-####`}
                            />
                        </Typography>
                    );
                }
            },
            {
                Header: 'Type',
                accessor: 'role',
                className: 'cell-center',
                Cell: ({ row }) => {
                    const { role, primary, source } = row.original;
                    if (source === 'manager') {
                        return <Chip {...getChipConfig(role, primary)} />;
                    }
                    if (source === 'member') {
                        return <MemberChip role={role} />;
                    }
                }
            },
            {
                Header: 'Role',
                accessor: row => formatRoleLabel(row.role),
                className: 'cell-center',
                Cell: ({ row }) => (
                    <Typography
                        sx={{ fontSize: '13px' }}
                        component="span"
                        color="text.primary"
                    >
                        {formatRoleLabel(row.original.role)}
                    </Typography>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ row }) => {
                    const { status, source } = row.original;
                    const { label, color } = getStatusChipConfig(status, source);
                    return <Chip size="small" label={label} color={color} />;
                }
            },

            ...(canManage
                ? [
                      {
                          Header: 'Actions',
                          className: 'cell-right',
                          Cell: ({ row }) => (
                              <>
                                  <IconButton
                                      onClick={e => {
                                          e.stopPropagation();
                                          handleMenuClick(e, row.original);
                                      }}
                                  >
                                      <MoreHoriz />
                                  </IconButton>
                              </>
                          )
                      }
                  ]
                : [])
        ],
        [canManage]
    );

    return (
        <>
            <Table
                columns={columns}
                data={data}
                externalGlobalFilter={globalFilter}
            />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                {selectedMember?.source === 'member' &&
                    selectedMember?.status === INVITE_STATUS.PENDING_APPROVAL &&
                    onInvitedMemberApprove && (
                        <MenuItem onClick={handleApproveClick}>Approve</MenuItem>
                    )}
                <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
            </Menu>
        </>
    );
};

export default InvitedOrgMembersTable;
